import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { t, chips, money, dateTime, errorText } from '../i18n/index.ts';
import { api } from '../lib/api.ts';
import { Avatar, ErrorView, InlineCards, Loading, Money, PlayingCard, Status, useToast } from '../components/ui.tsx';
import type { tournamentResults } from '../../server/domain/debts.ts';
import type { HandSummary } from '../../server/domain/game.ts';
import type { HandView } from '../../server/engine/view.ts';
import { HandEventList } from './Table.tsx';
import { HandVerifier } from './Fairness.tsx';
import { makeFormatter, useAmountUnit } from '../lib/amounts.ts';
import { useSession } from '../lib/session.tsx';import { SHOW_FAIRNESS } from '../lib/fairness.ts';


type Results = ReturnType<typeof tournamentResults>;
type HandListItem = { handNo: number; startedAt: number; completedAt: number; summary: HandSummary };

const placeLabel = (from: number | null, to: number | null) => (from === null ? '–' : from === to ? `${from}.` : `${from}.–${to}.`);

export function ResultsPage() {
  const { id } = useParams();
  const { me } = useSession();
  const results = useQuery({ queryKey: ['results', id], queryFn: () => api.get<Results>(`/api/tournaments/${id}/results`) });
  const [pages, setPages] = useState<HandListItem[][]>([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const toast = useToast();
  const hands = useQuery({ queryKey: ['hands', id], queryFn: () => api.get<{ hands: HandListItem[] }>(`/api/tournaments/${id}/hands?limit=30`) });
  if (results.isPending) return <Loading />;
  if (results.isError) return <ErrorView error={results.error} onRetry={() => results.refetch()} />;
  const r = results.data;
  const names = new Map(r.standings.map((s) => [s.user.id, s.user.displayName]));
  const nameOf = (uid: string) => names.get(uid) ?? '?';
  const allHands = [...(hands.data?.hands ?? []), ...pages.flat()];
  const lastLoaded = allHands.at(-1);
  const finished = r.tournament.status === 'finished';
  return (
    <div>
      <section className="section" aria-labelledby="standings-h">
        <div className="section-head">
          <h2 id="standings-h">{t('results.title')}</h2>
          <span className="hint">{t('results.pool', { amount: money(r.poolCents) })}</span>
        </div>
        {!finished && <p className="alert">{t('results.notFinished')}</p>}
        <div className="table-scroll panel" style={{ padding: 0 }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>{t('results.place')}</th>
                <th>{t('results.player')}</th>
                <th className="r">{finished ? t('results.prize') : t('table.stackLabel')}</th>
                {finished && <th className="r">{t('results.net')}</th>}
              </tr>
            </thead>
            <tbody>
              {r.standings.map((s) => (
                <tr key={s.user.id} style={s.user.id === me?.id ? { background: 'var(--paper-2)' } : undefined}>
                  <td className="num">{placeLabel(s.placeFrom, s.placeTo)}</td>
                  <td>
                    <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
                      <Avatar name={s.user.displayName} url={s.user.avatarUrl} size="s" />
                      {s.user.displayName}
                    </span>
                  </td>
                  <td className="r">{finished ? <Money cents={s.prizeCents ?? 0} /> : chips(s.stack ?? 0)}</td>
                  {finished && (
                    <td className="r">
                      <Money cents={s.netCents ?? 0} signed />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {finished && (
        <section className="section" aria-labelledby="tdebts-h">
          <div className="section-head">
            <h2 id="tdebts-h">{me?.role === 'superadmin' ? t('results.debtsTitle') : t('results.yourDebts')}</h2>
            <Link to="/velat">{t('nav.debts')}</Link>
          </div>
          {r.debts.length === 0 ? (
            <div className="rows">
              <p className="empty">{t('results.noDebts')}</p>
            </div>
          ) : (
            <ul className="rows">
              {r.debts.map((d) => (
                <li key={d.id} className="row">
                  <div className="row-main">
                    <div className="row-title">
                      {d.debtor.displayName} → {d.creditor.displayName}
                    </div>
                    {d.kind === 'correction' && <span className="chip-tag">{t('debts.correction')}</span>}
                  </div>
                  <div className="row-end">
                    <Money cents={d.amountCents} />
                    <div>
                      <Status kind={d.status}>{t(`debts.status.${d.status}`)}</Status>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {r.corrections.length > 0 && (
        <section className="section">
          <h2>{t('results.corrections')}</h2>
          <ul className="rows">
            {r.corrections.map((c) => (
              <li key={c.id} className="row">
                <div className="row-main">
                  <div className="row-title">{c.reason}</div>
                  <div className="row-sub">
                    {dateTime(c.createdAt)}, {c.createdBy?.displayName}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="section" aria-labelledby="hands-h">
        <h2 id="hands-h">{t('results.handsTitle')}</h2>
        {hands.isError && <ErrorView error={hands.error} onRetry={() => hands.refetch()} />}
        {allHands.length === 0 && hands.data ? (
          <div className="rows">
            <p className="empty">{t('results.noHands')}</p>
          </div>
        ) : (
          <ul className="rows">
            {allHands.map((h) => (
              <li key={h.handNo}>
                <Link className="row" to={`/turnaukset/${id}/jaot/${h.handNo}`}>
                  <div className="row-main">
                    <div className="row-title">{t('table.handNo', { n: h.handNo })}</div>
                    <div className="row-sub">
                      {h.summary.pots
                        .flatMap((p) => p.winners.map((w) => t('table.wonPot', { name: nameOf(w.userId), amount: chips(w.amount) })))
                        .join(', ')}
                      {h.summary.pots[0]?.category ? ` ${t('table.withHand', { hand: t(`hands.${h.summary.pots[0].category as 'flush'}`) })}` : ''}
                    </div>
                  </div>
                  <div className="row-end">
                    {h.summary.board.length > 0 && <InlineCards cards={h.summary.board} />}
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
        {lastLoaded && lastLoaded.handNo > 1 && (pages.at(-1) ?? hands.data?.hands ?? []).length >= 30 && (
          <button
            className="btn"
            style={{ marginTop: 12 }}
            disabled={loadingMore}
            onClick={async () => {
              setLoadingMore(true);
              try {
                const res = await api.get<{ hands: HandListItem[] }>(`/api/tournaments/${id}/hands?limit=30&before=${lastLoaded.handNo}`);
                setPages((p) => [...p, res.hands]);
              } catch (err) {
                toast(errorText(err), 'error');
              } finally {
                setLoadingMore(false);
              }
            }}
          >
            {t('results.loadMore')}
          </button>
        )}
      </section>
    </div>
  );
}

export function HandDetailPage() {
  const { id, no } = useParams();
  const q = useQuery({
    queryKey: ['hand', id, no],
    queryFn: () => api.get<{ handNo: number; status: string; startedAt: number; completedAt: number | null; view: HandView }>(`/api/tournaments/${id}/hands/${no}`),
  });
  const table = useQuery({ queryKey: ['results', id], queryFn: () => api.get<Results>(`/api/tournaments/${id}/results`) });
  const [unit] = useAmountUnit();
  if (q.isPending) return <Loading />;
  if (q.isError) return <ErrorView error={q.error} onRetry={() => q.refetch()} />;
  const v = q.data.view;
  const names = new Map((table.data?.standings ?? []).map((s) => [s.user.id, s.user.displayName]));
  const fmt = makeFormatter(unit, v.level.bb);
  const seatName = (seat: number) => names.get(v.players.find((p) => p.seat === seat)?.userId ?? '') ?? `#${seat}`;
  return (
    <div className="page-narrow">
      <Link to={`/turnaukset/${id}/tulokset`}>{t('app.back')}</Link>
      <h2>{t('results.handDetail', { n: v.handNo })}</h2>
      <p className="hint">
        {dateTime(q.data.startedAt)}, {t('table.level', { n: '', sb: chips(v.level.sb), bb: chips(v.level.bb) }).replace(/^[^:]*:\s*/, '')}
      </p>
      <div className="panel">
        <div className="board" style={{ marginBottom: 12 }}>
          {v.board.map((c) => (
            <PlayingCard key={c} card={c} size="m" />
          ))}
        </div>
        <ul className="rows" style={{ marginBottom: 16 }}>
          {v.players.map((p) => (
            <li key={p.seat} className="row">
              <div className="row-main">
                <div className="row-title">
                  {seatName(p.seat)} {p.seat === v.buttonSeat && <span className="chip-tag dealer">D</span>}
                </div>
                <div className="row-sub num">
                  {fmt(p.startStack)} → {fmt(p.stack)}
                </div>
              </div>
              <div className="cards cards-tight">
                {p.hole ? p.hole.map((c) => <PlayingCard key={c} card={c} size="s" />) : <span className="hint">{t('table.hidden')}</span>}
              </div>
            </li>
          ))}
        </ul>
        <HandEventList log={v.log} name={seatName} fmt={fmt} keyPrefix={`detail-${v.handNo}`} />
        {v.result && Object.entries(v.result.hands).length > 0 && (
          <ul className="log" style={{ padding: 0 }}>
            {Object.entries(v.result.hands).map(([seat, h]) => (
              <li key={seat}>
                {seatName(Number(seat))}: {t(`hands.${h.category}`)} (<InlineCards cards={h.best5} />)
              </li>
            ))}
          </ul>
        )}
      </div>
      {SHOW_FAIRNESS && (
        <section className="panel" id="tarkista" aria-labelledby="verify-h" style={{ marginTop: 16 }}>
          <h3 id="verify-h">
            <span aria-hidden="true">🔒 </span>
            {t('fair.verifyTitle')}
          </h3>
          <HandVerifier
            tournamentId={id!}
            handNo={v.handNo}
            history={{
              board: v.board,
              shown: v.log.flatMap((e) => (e.t === 'reveal' ? [{ seat: e.seat, cards: e.cards }] : [])),
            }}
          />
        </section>
      )}
    </div>
  );
}
